import React from 'react';
import classNames from 'classnames';

import Typography from '../../Typography/Typography.js';

export interface RadioButtonProps
  extends Omit<
    React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    >,
    'type'
  > {
  /**
   * A label to pass to the radio button element.
   */
  label: string;

  /**
   * If true, the input and font-size will be smaller.
   * @default false
   */
  small?: boolean;
}

/**
 * Radio buttons are used when you have multiple options that a user can select
 * from, but only 1 selection is allowed.
 *
 * Regular radio buttons are used in consumer facing products without an admin
 * interface, such as our patient apps & scheduling apps.
 *
 * Small radio buttons are used in our admin, staff, and analyst apps.
 * These are used typically on tooling products.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=1902%3A1428
 */
const RadioButton = React.forwardRef(
  (
    { className, label, small = false, ...rest }: RadioButtonProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    return (
      <label className={className}>
        <input type="radio" className="radio-button" {...rest} ref={ref} />
        <Typography
          variant={small ? 'paragraph-medium' : 'title-large'}
          className={classNames('radio-button-label', { 'size-small': small })}
        >
          {label}
        </Typography>
      </label>
    );
  },
);

RadioButton.displayName = 'RadioButton';

export default RadioButton;
